import {
  useMutation
} from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryClient,
  UseMutationOptions,
  UseMutationResult
} from '@tanstack/react-query';

import {
  useCallback
} from 'react';

import type {
  AcceptInvitationParams,
  CreateInvitationExistingModel,
  GetOnboardingRequestByIdResponse,
  GetOnboardingRequestDetailResponse,
  ProblemDetails,
  SendDoka360InvitationModel
} from '../../models';

import { useCustomWelcomeClient } from '../../custom-client.tsx';
import type { ErrorType } from '../../custom-client.tsx';

type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;




/**
 * Accepts invitation and returns request details<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useAcceptInvitationHook = () => {
        const acceptInvitation = useCustomWelcomeClient<GetOnboardingRequestDetailResponse>();

        return useCallback((
    id: string,
    params: AcceptInvitationParams,
 signal?: AbortSignal
) => {


        return acceptInvitation(
          {url: `/invitations/${id}/accept`, method: 'POST',
        params, signal
    },
          );
        }, [acceptInvitation])
      }




export const useAcceptInvitationMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useAcceptInvitationHook>>>, TError,AcceptInvitationMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useAcceptInvitationHook>>>, TError,AcceptInvitationMutationVariables, TContext> => {

const mutationKey = ['acceptInvitation'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const acceptInvitation =  useAcceptInvitationHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useAcceptInvitationHook>>>, AcceptInvitationMutationVariables> = (props) => {
          const {id,params} = props ?? {};

          return  acceptInvitation(id,params,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AcceptInvitationMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useAcceptInvitationHook>>>>

    export type AcceptInvitationMutationError = ErrorType<ProblemDetails>
    export type AcceptInvitationMutationVariables = {id: string;params: AcceptInvitationParams}

    export const useAcceptInvitation = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useAcceptInvitationHook>>>, TError,AcceptInvitationMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useAcceptInvitationHook>>>,
        TError,
        AcceptInvitationMutationVariables,
        TContext
      > => {
      return useMutation(useAcceptInvitationMutationOptions(options), queryClient);
    }
    /**
 * Resend the invitation mail for a request in the Invited state.<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-backoffice
 */
export const useResendInvitationHook = () => {
        const resendInvitation = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
 signal?: AbortSignal
) => {


        return resendInvitation(
          {url: `/invitations/${id}/resend`, method: 'POST', signal
    },
          );
        }, [resendInvitation])
      }




export const useResendInvitationMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useResendInvitationHook>>>, TError,ResendInvitationMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useResendInvitationHook>>>, TError,ResendInvitationMutationVariables, TContext> => {

const mutationKey = ['resendInvitation'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const resendInvitation =  useResendInvitationHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useResendInvitationHook>>>, ResendInvitationMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  resendInvitation(id,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResendInvitationMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useResendInvitationHook>>>>

    export type ResendInvitationMutationError = ErrorType<ProblemDetails>
    export type ResendInvitationMutationVariables = {id: string}

    export const useResendInvitation = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useResendInvitationHook>>>, TError,ResendInvitationMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useResendInvitationHook>>>,
        TError,
        ResendInvitationMutationVariables,
        TContext
      > => {
      return useMutation(useResendInvitationMutationOptions(options), queryClient);
    }
    /**
 * Creates an invitation for an existing customer<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-backoffice
 */
export const useCreateInvitationForExistingCustomerHook = () => {
        const createInvitationForExistingCustomer = useCustomWelcomeClient<GetOnboardingRequestByIdResponse>();

        return useCallback((
    createInvitationExistingModel: CreateInvitationExistingModel,
 signal?: AbortSignal
) => {


        return createInvitationForExistingCustomer(
          {url: `/invitations/existing`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createInvitationExistingModel, signal
    },
          );
        }, [createInvitationForExistingCustomer])
      }




export const useCreateInvitationForExistingCustomerMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateInvitationForExistingCustomerHook>>>, TError,CreateInvitationForExistingCustomerMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateInvitationForExistingCustomerHook>>>, TError,CreateInvitationForExistingCustomerMutationVariables, TContext> => {

const mutationKey = ['createInvitationForExistingCustomer'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const createInvitationForExistingCustomer =  useCreateInvitationForExistingCustomerHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useCreateInvitationForExistingCustomerHook>>>, CreateInvitationForExistingCustomerMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createInvitationForExistingCustomer(data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateInvitationForExistingCustomerMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateInvitationForExistingCustomerHook>>>>
    export type CreateInvitationForExistingCustomerMutationBody = CreateInvitationExistingModel
    export type CreateInvitationForExistingCustomerMutationError = ErrorType<ProblemDetails>
    export type CreateInvitationForExistingCustomerMutationVariables = {data: CreateInvitationExistingModel}

    export const useCreateInvitationForExistingCustomer = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateInvitationForExistingCustomerHook>>>, TError,CreateInvitationForExistingCustomerMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useCreateInvitationForExistingCustomerHook>>>,
        TError,
        CreateInvitationForExistingCustomerMutationVariables,
        TContext
      > => {
      return useMutation(useCreateInvitationForExistingCustomerMutationOptions(options), queryClient);
    }
    /**
 * Sends a doka360 invitation for an existing customer<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-backoffice
 */
export const useSendDoka360InvitationForExistingCustomerHook = () => {
        const sendDoka360InvitationForExistingCustomer = useCustomWelcomeClient<void>();

        return useCallback((
    sendDoka360InvitationModel: SendDoka360InvitationModel,
 signal?: AbortSignal
) => {


        return sendDoka360InvitationForExistingCustomer(
          {url: `/invitations/existing/doka360`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: sendDoka360InvitationModel, signal
    },
          );
        }, [sendDoka360InvitationForExistingCustomer])
      }




export const useSendDoka360InvitationForExistingCustomerMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSendDoka360InvitationForExistingCustomerHook>>>, TError,SendDoka360InvitationForExistingCustomerMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSendDoka360InvitationForExistingCustomerHook>>>, TError,SendDoka360InvitationForExistingCustomerMutationVariables, TContext> => {

const mutationKey = ['sendDoka360InvitationForExistingCustomer'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const sendDoka360InvitationForExistingCustomer =  useSendDoka360InvitationForExistingCustomerHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useSendDoka360InvitationForExistingCustomerHook>>>, SendDoka360InvitationForExistingCustomerMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  sendDoka360InvitationForExistingCustomer(data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SendDoka360InvitationForExistingCustomerMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useSendDoka360InvitationForExistingCustomerHook>>>>
    export type SendDoka360InvitationForExistingCustomerMutationBody = SendDoka360InvitationModel
    export type SendDoka360InvitationForExistingCustomerMutationError = ErrorType<ProblemDetails>
    export type SendDoka360InvitationForExistingCustomerMutationVariables = {data: SendDoka360InvitationModel}

    export const useSendDoka360InvitationForExistingCustomer = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSendDoka360InvitationForExistingCustomerHook>>>, TError,SendDoka360InvitationForExistingCustomerMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useSendDoka360InvitationForExistingCustomerHook>>>,
        TError,
        SendDoka360InvitationForExistingCustomerMutationVariables,
        TContext
      > => {
      return useMutation(useSendDoka360InvitationForExistingCustomerMutationOptions(options), queryClient);
    }
